<template>
  <validation-observer
    ref="observer"
    v-slot="{invalid}"
  >
    <b-modal
      ref="modal"
      v-model="modal.isOpen"
      :title="getModalTitle"
      :ok-title="getModalSubmitButtonText"
      :ok-disabled="invalid"
      @ok.prevent="onSubmit"
      @hide="onHide"
      @show="onShow"
    >
      <div class="custom__multiselect">
        <validation-provider
          v-slot="{errors, valid, touched}"
          rules="required"
          name="Domain name"
        >
          <b-form-group label="Domain name *">
            <div class="input__wrapper--tag">
              <b-form-input
                id="tag--name"
                v-model="name"
                class="tag-modal-window"
                :state="(!touched && !valid) ? null : valid ? null : false"
                @focus="onFocus"
              />
              <b-spinner
                v-if="isLoading && !isEditMode"
                class="autocomplete__spinner"
                variant="success"
                type="grow"
              />
            </div>
            <div
              v-if="isVisible && !isEditMode"
              class="area"
            >
              <template v-if="allTagsRenderList">
                <p
                  v-for="tag of allTagsRenderList"
                  :id="`${tag.tagGuid}-tagList-item-tag`"
                  :key="tag.tagGuid"
                  class="tag--item"
                  @click="createTag(tag)"
                  v-html="formattedItemName(tag.tagName)"
                />
              </template>
            </div>
            <input-error-message :error="errors" />
          </b-form-group>
        </validation-provider>
      </div>
      <b-form-group label="Description">
        <b-form-textarea
          id="tag-description"
          v-model="description"
          no-resize
          rows="2"
        />
      </b-form-group>
      <colors-editor-component
        size="small"
        :text-color="textColor"
        :background-color="backgroundColor"
        @get-background-color="getBackgroundColor"
        @get-text-color="getTextColor"
      />
      <tag-preview
        :background-color="backgroundColor"
        :color="textColor"
        :name="name"
      />
    </b-modal>
  </validation-observer>
</template>

<script>
import {createNamespacedHelpers} from 'vuex';
import InputErrorMessage from '@/modules/DataManagement/components/InputErrorMessage.vue';
import ColorsEditorComponent
  from '@/modules/DataManagement/views/designer/components/editorComponents/ColorsEditorComponent.vue';
import TagPreview from '@/modules/DataManagement/components/TagPreview.vue';
import ContentSpinner from '@/core/components/ContentSpinner.vue';
import {setAddEditTagModalState} from '@/store/modules/tags/actions';
import NoContent from '@/core/components/NoContent.vue';
import {highlightText} from '@/core/utils';

const {
  mapState: tagsMapState,
  mapActions: tagsMapActions,
} = createNamespacedHelpers('tags');

const {
  mapState: sidebarMapState,
} = createNamespacedHelpers('sidebar');

export default {
  name: 'AddEditTagModal',
  components: {
    NoContent,
    ContentSpinner,
    TagPreview,
    ColorsEditorComponent,
    InputErrorMessage,
  },
  data() {
    return {
      copy: null,
      showAddButton: false,
      name: '',
      description: '',
      textColor: '#000',
      backgroundColor: '#fff',
      tableGuid: null,
      isLoading: false,
      isValid: true,
      isVisible: false,
      existingTagGuid: null,
    };
  },
  computed: {
    ...tagsMapState({
      modal: state => state.addEditTagModal,
      isEditMode: state => state.addEditTagModal.isEditMode,
      allTags: state => state.allTags,
    }),
    ...sidebarMapState({
      tablesList: state => state.tablesList,
    }),
    filteredTags() {
      return this.allTags.filter(tag => (
        !this.tablesList[this.tableGuid].tags.some(el => el.tagGuid === tag.tagGuid)
      ));
    },
    allTagsRenderList() {
      if(!this.copy) return;
      if(this.name.length) {
        return this.copy.filter(el => el.tagName.toLowerCase().includes(this.name.toLowerCase()));
      }
      return this.filteredTags;
    },
    getModalTitle() {
      return !this.isEditMode ? 'Create new domain' : 'Update domain';
    },
    getModalSubmitButtonText() {
      return !this.isEditMode ? 'Create' : 'Update';
    },
  },
  methods: {
    ...tagsMapActions([
      'addNewTag',
      'setAddEditTagModalState',
      'getAllTags',
    ]),
    formattedItemName(name) {
      return highlightText(this.name, name);
    },
    async onFocus() {
      this.copy = JSON.parse(JSON.stringify(this.filteredTags));
      this.isVisible = true;
    },
    getBackgroundColor({color}) {
      this.backgroundColor = color;
    },
    getTextColor({color}) {
      this.textColor = color;
    },
    createTag(tag) {
      this.name = tag.tagName;
      this.description = tag.tagDescription;
      this.textColor = tag.textColor;
      this.backgroundColor = tag.backgroundColor;
      this.existingTagGuid = tag.tagGuid;
      this.isVisible = false;
    },
    async onSubmit() {
      if (!this.name.length) {
        this.isValid = false;
        return;
      }
      const payload = {
        tagName: this.name,
        tagDescription: this.description,
        textColor: this.textColor,
        backgroundColor: this.backgroundColor,
      };

      const isElementWithTheSameNameExist = this.filteredTags.some(tag => tag.tagName === this.name);
      if(isElementWithTheSameNameExist) {
        payload.tagGuid = this.existingTagGuid;
      } else {
        this.existingTagGuid = null;
      }

      if (this.isEditMode) {
        payload.tagGuid = this.modal.data.tagGuid;
      }
      const isSuccess = await this.addNewTag({
        tableId: this.tableGuid,
        payload,
        isEditMode: this.isEditMode,
      });

      this.$toasted[isSuccess ? 'success' : 'error'](
        isSuccess
          ? `Domain was ${this.isEditMode ? 'updated' : 'added'} successfully`
          : 'Something went wrong',
        {duration: TOASTER_DURATION},
      );

      if (isSuccess) {
        this.onHide();
      }
    },
    onHide() {
      this.setAddEditTagModalState({
        isOpen: false,
        data: null,
        isEditMode: false,
      });
      this.existingTagGuid = null;
      window.removeEventListener('click', this.onClickOutside);
    },
    onClickOutside(e) {
      if(!e.target.classList.contains('tag--item') && !e.target.classList.contains('tag-modal-window')) {
        this.isVisible = false;
      }
    },
    async onShow() {
      window.addEventListener('click', this.onClickOutside);
    },
  },
  async beforeMount() {
    this.isLoading = true;
    !this.allTags && await this.getAllTags();
    this.isLoading = false;
    this.tableGuid = this.$route.params.tableUuid;
    if (this.isEditMode) {
      this.name = this.modal.data.tagName;
      this.description = this.modal.data.tagDescription;
      this.textColor = this.modal.data.textColor || '#000';
      this.backgroundColor = this.modal.data.backgroundColor || '#fff';
    }
  },
};
</script>


<style scoped lang="scss">
.multiselect__content {
  min-height: 100px;
}
.no__result {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.area {
  background-color: #eee;
  position: absolute;
  right: 15px;
  left: 15px;
  transform: translateY(5px);
  box-shadow: 0 0 10px #ccc;
  z-index: 10;
  max-height: 200px;
  overflow-y: auto;
  border-bottom-left-radius: 6px;
  border-bottom-right-radius: 6px;

  p {
    padding: 5px;
    background-color: #fff;
    cursor: pointer;
    transition: 0.4s;

    &:not(:last-child) {
      margin-bottom: 1px;
    }

    &:last-child {
      margin-bottom: 0;
    }
    &:hover {
      background-color: #79c579;
      color: white;
    }
  }
}

.input__wrapper--tag {
  position: relative;
}

.autocomplete__spinner {
  position: absolute;
  width: 15px;
  height: 15px;
  right: 15px;
  top: 30%;
}
</style>